import {
  OutboundCarrierGateError,
  outboundCarrierBlock,
  readOutboundCarrierBlock,
} from '@/utils/fulfillmentOutboundGate';
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { PackingVideoInline } from '../components/PackingVideos';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FulfillmentAdminClient } from '../../../infrastructure/FulfillmentAdminClient';
import type {
  ClosingScanLine,
  ClosingSession,
  ClosingSessionDetail,
  ClosingSessionHistoryRow,
  ClosingSnapshot,
  ClosingTallies,
  ClosingTargetConflict,
  FulfillmentOrderLine,
  ProcessClosingScanOrderResponse,
} from '../../../infrastructure/FulfillmentAdminClient';
import { getAdminApiUrl, AdminApiDomain } from '../../../config/ApiRouting';
import { ROUTES } from '../../../routes/routes';
import { kstClock, kstDay, kstHourMinute, kstTimestamp } from '../../../utils/fulfillmentTime';
import { OperatorIdentity } from '../../../infrastructure/OperatorIdentity';
import { FulfillmentToastHost } from '../components/FulfillmentToast';
import { useFulfillmentToast } from '../components/useFulfillmentToast';
import { describeHold, describeViolations, serverRefusal, serverViolations } from '@/utils/fulfillmentErrors';
import { toScanAscii } from '@/utils/scanAscii';
import { AttemptKeys } from '@/utils/attemptKeys';
import { FulfillmentSound } from '../components/outboundSound';
import { speak } from '../components/fulfillmentVoice';
import { asciiScanField } from '../components/asciiScanField';
import { CameraScanField } from '../components/CameraScanField';
import { useScanInput } from '../components/useScanInput';

interface VerdictBadge {
  variant: string;
  label: string;
}

/** Why the confirm gate refused: standing warnings can be forced through, a count mismatch cannot. */
type ClosingBlockReason = 'WARNINGS' | 'COUNT_MISMATCH';

interface ClosingErrorBody {
  code?: string;
  operatingDay?: string;
  docId?: string;
  blockedBy?: ClosingBlockReason;
  /** A count of the standing warnings, never their text. */
  warnings?: number;
  okCount?: number;
  expectedCount?: number;
  /** The scan or order a refusal is about, when the server names it. */
  lineNo?: number;
  orderId?: number;
  waybill?: string | null;
  /** Why a scan's outbound record could not be closed; the closedBy pair comes only with ALREADY_CLOSED. */
  reason?: string;
  closedByDocId?: string;
  closedByOperatingDay?: string | null;
  message?: string | string[];
  /** On CLOSING_ORDER_ON_HOLD: every clean scan whose order is held; `orderId`/`lineNo` repeat only the first [SSD-7914]. */
  heldLines?: Array<{ orderId: number; lineNo: number }>;
}

interface ClosingBlock {
  reasons: ClosingBlockReason[];
  warnings: number | null;
  okCount: number | null;
  expectedCount: number | null;
}

interface TallyTile {
  label: string;
  value: number;
  danger?: boolean;
}

const VERDICT_BADGES: Record<string, VerdictBadge> = {
  OK: { variant: 'success', label: '✓ Outbounded' },
  NOT_OUTBOUNDED: { variant: 'danger', label: '⚠ Not outbounded' },
  UNKNOWN_WAYBILL: { variant: 'danger', label: '⚠ Unknown order' },
  // A code the system never knew and a code whose order is gone ask different things of the desk.
  ORDER_NOT_FOUND: { variant: 'danger', label: '⚠ Order no longer exists' },
  AMBIGUOUS_WAYBILL: { variant: 'danger', label: '⚠ Waybill on more than one order' },
  DUPLICATE: { variant: 'danger', label: '⚠ Duplicate scan' },
  // A hold can be a cancel request, so this box must not reach the carrier until someone has looked [SSD-7914].
  ON_HOLD: { variant: 'danger', label: '⚠ On hold' },
  DELETED: { variant: 'secondary', label: 'Deleted' },
};

const GENERIC_ERROR = 'The request failed. Please try again shortly.';
/** Read aloud when the verdict has no wording of its own. */
const VOICE_WARNING = 'Please check this order';
/** Spoken when the operator switches the voice alert back on, so the desk hears that it works. */
const VOICE_ENABLED = 'Voice alert on';
/** A waybill that resolves to no order at all, so the row can only be answered off the physical label. */
const UNKNOWN_WAYBILL_REMARK = 'Mistyped waybill or an order from another system — check the physical label';
/** The order behind this waybill has been removed since it shipped, so the parcel needs a person. */
const ORDER_NOT_FOUND_REMARK = 'The order this waybill named no longer exists — check with the order desk';
/** Two orders carry this number, so no box can be credited until one of them is corrected. */
const AMBIGUOUS_WAYBILL_REMARK = 'This waybill sits on more than one order — fix the duplicate before closing';
/** Removing the scan comes first and holds either way; a later scan is what reads the order again. */
const ON_HOLD_REMARK =
  'Remove this scan, set the box aside and lower the count by one. If its hold is cleared on Order Detail, scan the box again';
/** A box that scanned clean and was held afterwards: the server only finds it at confirm, so the row learns it from the refusal. */
const HELD_AFTER_SCAN_REMARK =
  'On hold at the last confirm. If the hold has been cleared, confirm again; otherwise remove this scan, set the box aside and lower the count by one';
/** Enough history for a closing to be reopened weeks later without turning the card into a report. */
const HISTORY_LIMIT = 20;
/** Same sentence for the first count and every re-count, because it is the same rule. */
const INVALID_COUNT = 'The count must be a whole number of 1 or more';
const MIN_WAYBILL_LENGTH = 5;
const INCOMPLETE_SCAN = 'Incomplete scan — scan again';
const isValidExpectedCount = (value: string): boolean =>
  value.trim() !== '' && Number.isInteger(Number(value)) && Number(value) > 0;
/** Past this a count is more likely a stray keystroke than a day's boxes, so it is asked about before it is saved. */
const LARGE_TARGET = 5000;

/** The verdict's own wording is spoken: one fixed sentence for every warning told the desk to look up but not what at. */
const speakWarning = (verdict?: string): void => speak(verdict ? describeVerdictAloud(verdict) : VOICE_WARNING);

/** The badge label without its warning glyph, which is what a voice can actually read out. */
const describeVerdictAloud = (verdict: string): string => {
  const label = VERDICT_BADGES[verdict]?.label;
  return label ? label.replace(/^[^A-Za-z]+/, '') : VOICE_WARNING;
};

/** A line already inbounded, in either of the two names that state carries. */
const INBOUNDED_STATES = ['INBOUNDED', 'READY'];
/** A line already shipped elsewhere; the server answers it as already outbounded, so it is no gap. */
const OUTBOUNDED_LINE_STATE = 'OUTBOUNDED';
/** Lines that can never rejoin the shippable set, so the completeness check ignores them. */
const EXITED_STATES = ['CANCELLED', 'RETURNED', 'CLOSED'];
/** Orders that have left the outbound flow for good; outbounding one from the closing desk is never the answer. */
const EXITED_ORDER_STATES = ['CANCELLED', 'RETURNED', 'RETURNED_PARTIAL', 'CLOSED', 'MERGED', 'RESOLVED'];
const EXITED_ORDER_REMARK = 'this order has left the outbound flow — remove the scan or check with the order desk';

/**
 * The same outbound predicate the server enforces, evaluated here only so the modal can hold the
 * button and say what is missing — closing may not open a side door through the shared gate.
 */
const describeInboundGap = (lines: FulfillmentOrderLine[]): string | null => {
  const shippable = lines.filter((line) => !EXITED_STATES.includes(line.lineStatus));
  if (shippable.length === 0) {
    return 'Cannot outbound — this order has no line left to ship.';
  }
  const short = shippable.filter(
    (line) => !INBOUNDED_STATES.includes(line.lineStatus) && line.lineStatus !== OUTBOUNDED_LINE_STATE,
  ).length;
  return short === 0
    ? null
    : `Cannot outbound — ${short} of ${shippable.length} items are not inbounded yet. Receive them on Order Detail first.`;
};

/** Controls the desk is deliberately using keep the cursor; everything else hands it back [T07]. */
const CURSOR_OWNERS = 'input, textarea, select, button, a, [role="dialog"]';

/** Every verdict that leaves a box uncredited; the summary must count the same set the tiles do. */
const UNKNOWN_VERDICTS = ['UNKNOWN_WAYBILL', 'ORDER_NOT_FOUND', 'AMBIGUOUS_WAYBILL'];
/** Every verdict that raised a warning badge; the row behind it is shaded so the log can be scanned for them. */
const WARNING_VERDICTS = ['NOT_OUTBOUNDED', ...UNKNOWN_VERDICTS, 'DUPLICATE', 'ON_HOLD'];

/** OPEN and AMENDING are the two states the desk is still scanning into. */
const isScannable = (status: string): boolean => status === 'OPEN' || status === 'AMENDING';

const percentOf = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0);

const pluralise = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** How far off the manual count the floor is, in the direction it is off. */
const describeGap = (remaining: number): string | null => {
  if (remaining > 0) return `(${remaining} short of the manual count)`;
  if (remaining < 0) return `(${-remaining} over the manual count)`;
  return null;
};

const describeProgress = (ok: number, warnings: number, expected: number, remaining: number): string => {
  const head = `Closing progress ${percentOf(ok, expected)}% — OK ${ok}/${expected}`;
  if (remaining === 0 && warnings === 0) return `${head} exact match · 0 warnings`;
  const gap = describeGap(remaining);
  return [head, gap, `· ${pluralise(warnings, 'warning')} to resolve`].filter(Boolean).join(' ');
};

/** What still stands between the operator and a confirm, named on the button itself. */
const describeConfirmBlockers = (warnings: number, remaining: number): string[] => {
  const blockers: string[] = [];
  if (remaining > 0) blockers.push(`${remaining} remaining`);
  if (remaining < 0) blockers.push(`${-remaining} over-scanned`);
  if (warnings > 0) blockers.push(pluralise(warnings, 'warning'));
  return blockers;
};

const closingErrorOf = (err: unknown) => (axios.isAxiosError<ClosingErrorBody>(err) ? err : undefined);

/**
 * A gateway that blinked and a row the server refuses to touch must not read as one generic sentence. The
 * gate's violations say why in words the desk can act on; the status is shown only when the server said nothing.
 */
const describeRequestFailure = (err: unknown): string => {
  if (err instanceof OutboundCarrierGateError) {
    return err.message;
  }
  const failure = closingErrorOf(err);
  const status = failure?.response?.status;
  if (status == null) {
    return 'The request failed — the server did not answer.';
  }
  const violations = serverViolations(err);
  const message = violations ? describeViolations(violations) : serverRefusal(err);
  return message ?? `The request failed — HTTP ${status}.`;
};

/** The refusal whose only subject is an internal tracking id; the sentence is recognised, never mined for the id. */
const UNCLOSABLE_OUTBOUND = /^Outbound tracking \S+ is missing or already closed$/;

/** The confirm refusals that name one scan; their body carries the order and waybill as fields. */
const CODED_SCAN_REFUSALS = ['CLOSING_OUTBOUND_UNCLOSABLE', 'CLOSING_LINE_ORDER_UNAVAILABLE'];
/** A held order the confirm refused whatever `force` said, so "confirm anyway" is never the way out [SSD-7914]. */
const ORDER_ON_HOLD = 'CLOSING_ORDER_ON_HOLD';

/** Every scan a hold refusal names, first one first; an older server sends only `lineNo` [SSD-7914]. */
const heldLineNosOf = (body: ClosingErrorBody): number[] => {
  const named = (body.heldLines ?? []).map((held) => held.lineNo).filter((lineNo) => Number.isFinite(lineNo));
  if (named.length > 0) {
    return Array.from(new Set(named));
  }
  return body.lineNo != null ? [body.lineNo] : [];
};

/**
 * Always built here: the server names the orders and the scans, and only the desk's next step is ours to word.
 * The count is part of the way out, because the confirm checks it first: a removed box leaves the day one short.
 */
const describeOrderOnHold = (body: ClosingErrorBody, lines: ClosingScanLine[]): string => {
  const lineNos = heldLineNosOf(body);
  if (lineNos.length > 1) {
    const scans = lineNos.join(', ');
    return `${lineNos.length} orders are on hold (scans ${scans}), so the day cannot close. Set those boxes aside, remove their scans and lower the count by ${lineNos.length} with Edit count, then confirm again. If a hold is no longer needed, clear it on Order Detail.`;
  }
  const line = lineNos.length === 1 ? lines.find((candidate) => candidate.lineNo === lineNos[0]) : undefined;
  const orderId = body.orderId ?? line?.orderId ?? null;
  const waybill = body.waybill ?? line?.waybill ?? null;
  const subject = `${orderId != null ? `Order ${orderId}` : 'An order'}${waybill ? ` (waybill ${waybill})` : ''}`;
  const scan = lineNos.length === 1 ? `scan ${lineNos[0]}` : 'its scan';
  return `${subject} is on hold, so the day cannot close. Set the box aside, remove ${scan} and lower the count by one with Edit count, then confirm again. If the hold is no longer needed, clear it on Order Detail.`;
};

/**
 * The sentence for a coded scan refusal when the server sent none, built from the same fields it names
 * the scan by, so the order and waybill reach the desk whichever way the body arrives.
 */
const describeCodedScanRefusal = (body: ClosingErrorBody, line: ClosingScanLine | undefined): string => {
  const waybill = body.waybill ?? line?.waybill ?? null;
  const waybillNote = waybill ? ` (waybill ${waybill})` : '';
  const scan = body.lineNo != null ? `scan ${body.lineNo}` : 'the scan';
  if (body.code === 'CLOSING_LINE_ORDER_UNAVAILABLE') {
    const subject = body.lineNo != null ? `Scan ${body.lineNo}` : 'A scan';
    return `${subject}${waybillNote} has no outbound order recorded; remove it and scan the box again`;
  }
  const orderId = body.orderId ?? line?.orderId ?? null;
  const subject = orderId != null ? `Order ${orderId}${waybillNote}` : `An order${waybillNote}`;
  if (body.reason === 'ALREADY_CLOSED') {
    const closing = body.closedByOperatingDay ? `the ${body.closedByOperatingDay} closing` : 'another closing';
    return `${subject} was already closed by ${closing}; remove ${scan} before confirming`;
  }
  if (body.reason === 'TRACKING_MISSING') {
    return `${subject} has no outbound record to close; check the order's outbound, or remove ${scan}`;
  }
  return `${subject} cannot be closed; check the order's outbound, or remove ${scan}`;
};

/**
 * A confirm refused over one order reads as that order, which the desk can find on the shelf, instead of
 * the internal id the server's sentence carries. The order comes off the body or off the scan it names.
 */
const describeConfirmRefusal = (err: unknown, lines: ClosingScanLine[]): string => {
  const body = closingErrorOf(err)?.response?.data;
  const line = body?.lineNo != null ? lines.find((candidate) => candidate.lineNo === body.lineNo) : undefined;
  if (body?.code === ORDER_ON_HOLD) {
    return describeOrderOnHold(body, lines);
  }
  const message = serverRefusal(err);
  if (body?.code != null && CODED_SCAN_REFUSALS.includes(body.code)) {
    // The server's sentence already names the order and waybill; it is only built here when the body has none.
    if (message) {
      return message;
    }
    const status = closingErrorOf(err)?.response?.status;
    return `${describeCodedScanRefusal(body, line)}${status != null ? ` (HTTP ${status})` : ''}`;
  }
  const orderId = body?.orderId ?? line?.orderId ?? null;
  const unclosable = message != null && UNCLOSABLE_OUTBOUND.test(message);
  if (!unclosable && (orderId == null || message?.includes(String(orderId)))) {
    return describeRequestFailure(err);
  }
  const subject =
    orderId != null
      ? `Order ${orderId}${line ? ` (waybill ${line.waybill})` : ''}`
      : 'An outbounded order in this session';
  if (!unclosable) {
    return `${subject}: ${describeRequestFailure(err)}`;
  }
  return `${subject} cannot be closed — its outbound record is missing or was already closed by another closing. Refresh and check the scan log.`;
};

/** A 4xx is an answer and repeating it changes nothing; only a blink is worth a second try. */
const isWorthRetrying = (err: unknown): boolean => {
  const status = closingErrorOf(err)?.response?.status;
  return status == null || status >= 500 || status === 429;
};

/** The session a stale target edit was refused against, when that is what the 409 says. */
const targetConflictOf = (err: unknown): ClosingTargetConflict | null => {
  const failure = axios.isAxiosError<Partial<ClosingTargetConflict>>(err) ? err : undefined;
  const body = failure?.response?.status === 409 ? failure.response.data : undefined;
  return body?.code === 'CONFLICT' && body.current
    ? { code: 'CONFLICT', current: body.current, ...(body.changedBy ? { changedBy: body.changedBy } : {}) }
    : null;
};

/** What the target editor read when it opened; a save only replaces that, never a colleague's newer one. */
interface TargetBase {
  docId: string;
  expectedCount: number | null;
  version: number | undefined;
}

const targetBaseOf = (session: Pick<ClosingSession, 'docId' | 'expectedCount' | 'version'>): TargetBase => ({
  docId: session.docId,
  expectedCount: session.expectedCount,
  version: session.version,
});

/** Says what moved under the editor and who moved it, so the operator knows which number to review before saving again. */
const describeTargetConflict = (
  base: TargetBase,
  current: ClosingTargetConflict['current'],
  changedBy?: string,
): string => {
  const who = changedBy ? `by ${changedBy}` : 'by someone else';
  if (!isScannable(current.status)) {
    return `This closing was ${current.status.toLowerCase()} ${who}, so the count can no longer be edited.`;
  }
  if (current.expectedCount !== base.expectedCount) {
    return `Target was changed to ${current.expectedCount ?? '—'} ${who}. Review and save again.`;
  }
  return `This closing was re-confirmed ${who}. Review the count and save again.`;
};

/** Scanner operators read wall-clock time, so the first-scan stamp stays HH:mm:ss — on the KST clock [G-16]. */
const formatScanTime = (iso: string | null): string => kstClock(iso);

/**
 * Older servers refuse without naming the reason, so it is read back off the same numbers they send —
 * a reason is never assumed, and the counts stay silent while they agree.
 */
const blockOf = (body: ClosingErrorBody): ClosingBlock | null => {
  const warnings = body.warnings ?? null;
  const okCount = body.okCount ?? null;
  const expectedCount = body.expectedCount ?? null;
  const reasons: ClosingBlockReason[] = [];
  if (body.blockedBy) {
    reasons.push(body.blockedBy);
  } else {
    if ((warnings ?? 0) > 0) reasons.push('WARNINGS');
    if (okCount != null && expectedCount != null && okCount !== expectedCount) reasons.push('COUNT_MISMATCH');
  }
  return reasons.length > 0 ? { reasons, warnings, okCount, expectedCount } : null;
};

const describeWarningBlock = (block: ClosingBlock): string =>
  block.warnings == null
    ? 'Unresolved warnings are standing on this session.'
    : `${block.warnings} unresolved warning${block.warnings === 1 ? '' : 's'} — clear them or confirm anyway.`;

const describeCountBlock = (block: ClosingBlock): string =>
  block.okCount == null || block.expectedCount == null
    ? 'The clean scans do not match the manual count.'
    : `${block.okCount} clean scans · manual count ${block.expectedCount} — the counts do not match.`;

const describeBlockReason = (block: ClosingBlock, reason: ClosingBlockReason): string =>
  reason === 'WARNINGS' ? describeWarningBlock(block) : describeCountBlock(block);

/** The day the desk is being sent to amend, when a start is refused because that day is already closed. */
interface ClosedDay {
  docId: string;
  operatingDay: string;
}

/** A history row is found by its KST day, so the started stamp keeps the date and drops the clock. */
const formatSessionDay = (iso: string | null): string => kstDay(iso);

const warningCountOf = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;

/**
 * A History row's warning record: what the log raised, how many were cleared, how many stood, and whether
 * the day was signed off over them. A server that sends no record reads as a dash, never as a clean day.
 */
function HistoryWarnings({ session }: { session: ClosingSessionHistoryRow }) {
  const raised = warningCountOf(session.warningsRaised);
  const cleared = warningCountOf(session.warningsCleared);
  const standing = warningCountOf(session.warningsStanding);
  const forced = session.forced === true && (
    <Badge bg="danger" className="ms-1" title="Confirmed despite warnings">
      Forced
    </Badge>
  );
  if (raised == null) {
    return <>{forced || <span className="text-muted">—</span>}</>;
  }
  if (raised === 0) {
    return (
      <>
        <span className="text-muted">None</span>
        {forced}
      </>
    );
  }
  return (
    <span className="text-nowrap">
      {raised} raised
      {cleared != null && <span className="text-muted"> · {cleared} cleared</span>}
      {standing != null &&
        (standing > 0 ? (
          <Badge bg="warning" text="dark" className="ms-1">
            {standing} standing
          </Badge>
        ) : (
          <span className="text-muted"> · 0 standing</span>
        ))}
      {forced}
    </span>
  );
}

/** A target the operator has to confirm before it is sent, because it is larger than any day's boxes. */
interface PendingTarget {
  action: 'start' | 'edit';
  count: number;
}

const formatCount = (count: number): string => count.toLocaleString('en-US');

/** An order that had already left is cleared without shipping it again, and the message has to say so. */
const describeProcessed = (result: ProcessClosingScanOrderResponse): string =>
  result.alreadyOutbounded
    ? `Order ${result.orderId} had already been outbounded — warning cleared, nothing shipped again`
    : `Order ${result.orderId} outbounded — warning resolved`;

/**
 * The note is read off the session view, never off the scan response, so it survives a reload.
 * Views that predate the persisted first scan still say what the verdict means, just without the original.
 */
const describeFirstScan = (line: ClosingScanLine): string =>
  line.firstScan
    ? `first #${line.firstScan.lineNo} ${formatScanTime(line.firstScan.at)} · ${line.firstScan.operator ?? '—'}`
    : 'already scanned in this session';

/**
 * What the confirm actually recorded. A forced confirm left warnings standing, and saying "every box
 * verified" over them is the screen contradicting its own record one line further down.
 */
const describeRecord = (snapshot: ClosingSnapshot): string => {
  if (snapshot.forced || snapshot.warnings > 0) {
    return `Closed with ${pluralise(snapshot.warnings, 'unresolved warning')}`;
  }
  return 'Today is closed — every box verified';
};

/** How the frozen numbers reconciled, in the same sentence the live run used while it was open. */
const describeRecordCounts = (snapshot: ClosingSnapshot): string =>
  `Manual count ${snapshot.expectedCount} · ${snapshot.ok} clean scans${
    snapshot.remaining > 0 ? ` · ${snapshot.remaining} short` : ''
  }${(snapshot.overScanned ?? 0) > 0 ? ` · ${snapshot.overScanned} over` : ''}`;

/** Who else's count this desk walked into, said once so nobody scans into a colleague's session unawares. */
const describeJoined = (loaded: ClosingSessionDetail): string =>
  `Joined the closing started by ${loaded.session.operator ?? '—'} at ${kstHourMinute(loaded.session.startedAt)} (${
    loaded.tallies.scanned
  } scanned)`;

/** A row's refusal is kept per session, because scan numbers start over in every one. */
const rowKeyOf = (docId: string, lineNo: number): string => `${docId}#${lineNo}`;

/** Over-scan is its own tile rather than a negative remaining, so neither signal hides the other [D-f]. */
const overScanOf = (tallies: ClosingTallies | undefined): number => tallies?.overScanned ?? 0;

export default function FulfillmentClosingPage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const scanInput = useScanInput();

  const [detail, setDetail] = useState<ClosingSessionDetail | null>(null);
  const [sessions, setSessions] = useState<ClosingSessionHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expectedCount, setExpectedCount] = useState('');
  const [waybill, setWaybill] = useState('');
  const [blocked, setBlocked] = useState<ClosingBlock | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ClosingScanLine | null>(null);
  const [voiceOn, setVoiceOn] = useState(true);
  // Going quiet is a decision; coming back is not, so only the mute is asked about [T19].
  const [muting, setMuting] = useState(false);
  const [scannerIdle, setScannerIdle] = useState(false);
  /** Codes that arrived while a request was in flight; a dropped scan is a box nobody counted [T07]. */
  const scanQueue = useRef<string[]>([]);
  const draining = useRef(false);
  /** One retry key per warning row: the server refuses a second key while the first is still resolving it. */
  const processKeys = useRef(new AttemptKeys());
  const [lastScan, setLastScan] = useState<{ docId: string; waybill: string; verdict: string } | null>(null);
  const [retryDelete, setRetryDelete] = useState<ClosingScanLine | null>(null);
  // null keeps the target locked; a string is the operator's draft, so an empty box is still edit mode.
  const [targetDraft, setTargetDraft] = useState<string | null>(null);
  const [targetError, setTargetError] = useState<string | null>(null);
  const [targetBase, setTargetBase] = useState<TargetBase | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [pendingProcess, setPendingProcess] = useState<ClosingScanLine | null>(null);
  const [zeroPackingVerified, setZeroPackingVerified] = useState(false);
  const [processMemo, setProcessMemo] = useState('');
  // null while the order is still being read; the gate stays shut until its lines have been seen.
  const [processGap, setProcessGap] = useState<string | null>(null);
  const [processLoaded, setProcessLoaded] = useState(false);
  const processReadGeneration = useRef(0);
  const closeProcess = () => {
    processReadGeneration.current += 1;
    setPendingProcess(null);
  };
  const [pendingAmend, setPendingAmend] = useState<ClosingSession | null>(null);
  // The day a refused start pointed at; the desk amends that one rather than forking the day.
  const [closedDay, setClosedDay] = useState<ClosedDay | null>(null);
  // A start that ran into a count someone else already opened; the desk sees whose before joining it.
  const [openElsewhere, setOpenElsewhere] = useState<ClosingSessionDetail | null>(null);
  const [pendingForce, setPendingForce] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<PendingTarget | null>(null);
  // The open session someone else started, as it stood when this page joined it on load.
  const [joined, setJoined] = useState<{ docId: string; text: string } | null>(null);
  // The last refusal each warning row met, shown under its badge until the row is resolved.
  const [rowRefusals, setRowRefusals] = useState<Record<string, string>>({});
  /** The scans the last hold refusal named; each confirm asks the server again, so it only lives until the next try [SSD-7914]. */
  const [heldRefusal, setHeldRefusal] = useState<{ docId: string; lineNos: number[] } | null>(null);
  /** The confirm refusal standing on screen; the same one again is not stacked under it. */
  const confirmRefusal = useRef<{ text: string; toastId: number } | null>(null);
  const { toasts, showSuccess, showError, dismiss } = useFulfillmentToast();
  // Read after an await, where the render's own list may already have been dismissed from.
  const standingToasts = useRef(toasts);
  useEffect(() => {
    standingToasts.current = toasts;
  }, [toasts]);

  /** Every refusal on this screen reads the same way, and is heard as well as seen [D-t]. */
  const reportFailure = useCallback(
    (err: unknown) => {
      showError(describeRequestFailure(err));
      FulfillmentSound.failure();
    },
    [showError],
  );

  /** Every closing action that lands says so in the one feedback slot the screen has [D-t]. */
  const reportSuccess = useCallback(
    (text: string) => {
      showSuccess(text);
    },
    [showSuccess],
  );

  const modalOpen =
    pendingDelete != null ||
    pendingProcess != null ||
    pendingAmend != null ||
    openElsewhere != null ||
    pendingTarget != null ||
    pendingForce ||
    cancelling ||
    muting;
  useEffect(() => {
    // A modal takes the cursor and never gives it back on close, so the next scan went into the void [T07].
    if (!modalOpen) {
      scanInput.reclaim();
    }
  }, [modalOpen, scanInput]);

  // Each read takes a number; only the newest one may land, so an older read finishing last cannot win.
  const detailRequestRef = useRef(0);
  const sessionsRequestRef = useRef(0);
  const refreshRequestRef = useRef(0);
  // Bumped whenever the operator picks or puts away a session, so a refresh started before that stands down.
  const sessionIntentRef = useRef(0);
  const detailRef = useRef<ClosingSessionDetail | null>(null);
  useLayoutEffect(() => {
    detailRef.current = detail;
  }, [detail]);

  /** Putting a session away is an operator decision: a read already in flight must not bring it back. */
  const dismissDetail = useCallback(() => {
    sessionIntentRef.current += 1;
    detailRequestRef.current += 1;
    setDetail(null);
  }, []);

  /** Resolves with the session only when this read is the one now on screen. */
  const loadSession = useCallback(
    async (docId: string): Promise<ClosingSessionDetail | null> => {
      const request = ++detailRequestRef.current;
      try {
        const session = await client.getClosingSession(docId);
        if (request === detailRequestRef.current) {
          setDetail(session);
          return session;
        }
        return null;
      } catch (err) {
        if (request === detailRequestRef.current) {
          throw err;
        }
        return null;
      }
    },
    [client],
  );

  const refreshSessions = useCallback(async () => {
    const request = ++sessionsRequestRef.current;
    try {
      const res = await client.listClosingSessions(HISTORY_LIMIT);
      const current = request === sessionsRequestRef.current;
      if (current) {
        setSessions(res.sessions);
      }
      return { sessions: res.sessions, current };
    } catch (err) {
      if (request === sessionsRequestRef.current) {
        throw err;
      }
      return { sessions: [], current: false };
    }
  }, [client]);

  /** The desk re-reads what the day now holds; a poll mid-scan would move the log under the scanner [D-p]. */
  const refreshView = useCallback(async () => {
    const request = ++refreshRequestRef.current;
    const sessionIntent = sessionIntentRef.current;
    try {
      const listed = await refreshSessions();
      if (!listed.current || request !== refreshRequestRef.current || sessionIntent !== sessionIntentRef.current) {
        return;
      }
      const docId = detailRef.current?.session.docId;
      if (docId) {
        await loadSession(docId);
      }
    } catch (err) {
      if (request === refreshRequestRef.current) {
        showError(describeRequestFailure(err));
      }
    }
  }, [refreshSessions, loadSession, showError]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        // The tab comes back with the old caret, so a half-typed code would take the next scan's tail [G-1].
        if (!modalOpen && document.activeElement === scanInput.ref.current) {
          scanInput.reclaim();
        }
        void refreshView();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshView, modalOpen, scanInput]);

  /** Opening a past session is a read: the blocked banner and the sign-off belong to the one being left. */
  const viewSession = async (docId: string) => {
    sessionIntentRef.current += 1;
    setBusy(true);
    setBlocked(null);
    try {
      await loadSession(docId);
    } catch (err) {
      reportFailure(err);
    } finally {
      setBusy(false);
    }
  };

  /** Leaving a past session goes back to the one the floor is scanning into, when there is one. */
  const closeView = async () => {
    const viewed = detail?.session.docId;
    const sessionIntent = ++sessionIntentRef.current;
    setBusy(true);
    try {
      const listed = await refreshSessions();
      if (!listed.current || sessionIntent !== sessionIntentRef.current) {
        return;
      }
      const working = listed.sessions.find((session) => isScannable(session.status) && session.docId !== viewed);
      if (working) {
        await loadSession(working.docId);
      } else {
        dismissDetail();
      }
    } catch (err) {
      if (sessionIntent === sessionIntentRef.current) {
        dismissDetail();
        reportFailure(err);
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    refreshSessions()
      .then(async (listed) => {
        // The day flow still lands straight on the one session being scanned into; the rest wait in the
        // history. An amendment counts: it survives a reload exactly like an open count and resumes here.
        const open = listed.sessions.find((session) => isScannable(session.status));
        if (ignore || !listed.current || !open) {
          return;
        }
        const loaded = await loadSession(open.docId);
        const me = OperatorIdentity.current();
        // Joining one's own session is the normal reload; only a colleague's is worth saying out loud, and
        // without a signed-in handle there is no telling whose it is.
        if (!ignore && loaded && me && loaded.session.operator && loaded.session.operator.toLowerCase() !== me) {
          setJoined({ docId: loaded.session.docId, text: describeJoined(loaded) });
        }
      })
      .catch(() => {
        // A failed read is an error state, never an empty history the desk would read as "no sessions" [T03].
        if (!ignore) showError(GENERIC_ERROR);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [refreshSessions, loadSession]);

  const startSession = async (largeTargetConfirmed = false) => {
    if (!isValidExpectedCount(expectedCount)) {
      return;
    }
    if (!largeTargetConfirmed && Number(expectedCount) > LARGE_TARGET) {
      setPendingTarget({ action: 'start', count: Number(expectedCount) });
      return;
    }
    setBusy(true);
    setClosedDay(null);
    try {
      const started = await client.startClosing({
        expectedCount: Number(expectedCount),
      });
      await loadSession(started.docId);
      await refreshSessions();
      reportSuccess(`Closing started — ${expectedCount} boxes to outbound`);
    } catch (err) {
      const existing = closingErrorOf(err);
      const body = existing?.response?.status === 409 ? existing.response.data : undefined;
      // The day is already closed: amending that session is the only correct move, so offer it [T15].
      if (body?.code === 'CLOSING_EXISTS_FOR_DATE' && body.docId) {
        setClosedDay({ docId: body.docId, operatingDay: body.operatingDay ?? formatSessionDay(null) });
        showError(serverRefusal(err) ?? 'That day is already closed.');
      } else if (body?.docId) {
        // An OPEN session is not a failure, but it may be a colleague's: say whose and since when before joining it.
        try {
          setOpenElsewhere(await client.getClosingSession(body.docId));
        } catch (readErr) {
          reportFailure(readErr);
        }
      } else {
        reportFailure(err);
      }
    } finally {
      setBusy(false);
    }
  };

  const continueOpenSession = async () => {
    const docId = openElsewhere?.session.docId;
    setOpenElsewhere(null);
    if (!docId) {
      return;
    }
    setBusy(true);
    try {
      await loadSession(docId);
      await refreshSessions();
    } catch (err) {
      reportFailure(err);
    } finally {
      setBusy(false);
    }
  };

  /** One code at a time, in scan order: the scanner outruns the round trip and a refused barcode is a lost box. */
  const drainScans = async (docId: string) => {
    if (draining.current) return;
    draining.current = true;
    setBusy(true);
    try {
      // A barcode submitted during the reload finds draining still set and returns early, so the queue is
      // re-checked after every reload and the loop only stops once a reload ends with nothing waiting.
      do {
        for (let scanned = scanQueue.current.shift(); scanned != null; scanned = scanQueue.current.shift()) {
          try {
            const result = await client.scanClosing(docId, { waybill: scanned });
            setLastScan({ docId, waybill: scanned, verdict: result.verdict });
            // The verdict is announced before the reload so the next barcode can already be on its way.
            if (result.verdict === 'OK') {
              FulfillmentSound.success();
            } else {
              FulfillmentSound.warning();
              if (voiceOn) {
                speakWarning(result.verdict);
              }
            }
          } catch (err) {
            reportFailure(err);
          }
        }
        try {
          await loadSession(docId);
        } catch (err) {
          // The scans already landed; a failed re-read must not leave the barcodes behind it unsent.
          showError(describeRequestFailure(err));
        }
      } while (scanQueue.current.length > 0);
    } finally {
      draining.current = false;
      setBusy(false);
      // The next barcode may already be half-typed into the box; the cursor comes back without cutting it.
      scanInput.reclaim();
    }
  };

  const submitScan = async (event: React.FormEvent) => {
    event.preventDefault();
    // A waybill scanned with the keyboard in Korean mode is read as the ASCII the scanner meant [SSD-7050].
    const scanned = toScanAscii(waybill).trim();
    if (!detail || !scanned) return;
    if (scanned.length < MIN_WAYBILL_LENGTH) {
      setWaybill('');
      showError(INCOMPLETE_SCAN);
      FulfillmentSound.failure();
      scanInput.ref.current?.focus();
      return;
    }
    scanQueue.current.push(scanned);
    // Cleared here rather than after the response, so the next barcode cannot append to this one.
    setWaybill('');
    await drainScans(detail.session.docId);
  };

  const deleteScan = async (line: ClosingScanLine) => {
    if (!detail) return;
    setPendingDelete(null);
    setBusy(true);
    setRetryDelete(null);
    try {
      await client.deleteClosingScan(detail.session.docId, line.lineNo);
      await loadSession(detail.session.docId);
      reportSuccess(`Scan #${line.lineNo} removed — ${line.waybill}`);
    } catch (err) {
      reportFailure(err);
      // The same scan is offered back so a blink costs one click, not a hunt down the log for the row.
      if (isWorthRetrying(err)) setRetryDelete(line);
    } finally {
      setBusy(false);
    }
  };

  const saveTarget = async (largeTargetConfirmed = false) => {
    if (!detail || targetDraft == null) return;
    if (!isValidExpectedCount(targetDraft)) {
      // The old target stands and the field stays open: a rejected edit must not silently re-lock a stale number.
      setTargetError(INVALID_COUNT);
      return;
    }
    const docId = detail.session.docId;
    const base = targetBase?.docId === docId ? targetBase : targetBaseOf(detail.session);
    // A large target already saved was asked about when it was saved; only a new one is asked about again.
    if (!largeTargetConfirmed && Number(targetDraft) > LARGE_TARGET && Number(targetDraft) !== base.expectedCount) {
      setPendingTarget({ action: 'edit', count: Number(targetDraft) });
      return;
    }
    setBusy(true);
    try {
      await client.editClosingTarget(docId, {
        expectedCount: Number(targetDraft),
        ...(base.expectedCount != null && { previousExpectedCount: base.expectedCount }),
        ...(base.version != null && { expectedVersion: base.version }),
      });
      setTargetDraft(null);
      setTargetError(null);
      setTargetBase(null);
      // Save turns into Edit count under the cursor, so the next scan's Enter reopened the count [8d].
      // Handed back once the count is saved, so a failed re-read cannot leave the cursor on the button.
      scanInput.reclaim();
      await loadSession(detail.session.docId);
      await refreshSessions();
      reportSuccess(`Manual count saved — ${targetDraft} boxes`);
    } catch (err) {
      const conflict = targetConflictOf(err);
      if (!conflict) {
        reportFailure(err);
        return;
      }
      // A colleague's newer save stands: redraw it, keep the typed draft, and let the next save replace it knowingly.
      const { current } = conflict;
      const message = describeTargetConflict(base, current, conflict.changedBy);
      setDetail((shown) =>
        shown?.session.docId === docId
          ? {
              ...shown,
              session: {
                ...shown.session,
                status: current.status,
                expectedCount: current.expectedCount,
                version: current.version,
              },
            }
          : shown,
      );
      setTargetBase(targetBaseOf(current));
      setTargetError(message);
      if (!isScannable(current.status)) {
        setTargetDraft(null);
      }
      showError(message);
      FulfillmentSound.failure();
      // The tallies and remaining count follow the target, so the rest of the session is read again too;
      // a failed re-read leaves them stale, and the operator has to know that before saving again.
      try {
        await loadSession(docId);
      } catch (reloadErr) {
        showError(`The session could not be reloaded — press Refresh. ${describeRequestFailure(reloadErr)}`);
      }
      await refreshSessions().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const cancelSession = async () => {
    if (!detail) return;
    setCancelling(false);
    setBusy(true);
    try {
      await client.cancelClosing(detail.session.docId);
      // Back to the count entry: the cancelled session keeps its rows, but nothing of it is on screen.
      dismissDetail();
      setExpectedCount('');
      setBlocked(null);
      await refreshSessions();
      reportSuccess('Closing cancelled — start again from the count');
    } catch (err) {
      reportFailure(err);
    } finally {
      setBusy(false);
    }
  };

  /** Pins a refusal under the row's badge, or clears it once the row went through. */
  const noteRowRefusal = (docId: string, lineNo: number, text: string | null) => {
    setRowRefusals((current) => {
      const key = rowKeyOf(docId, lineNo);
      if (text != null) {
        return { ...current, [key]: text };
      }
      if (!(key in current)) {
        return current;
      }
      const rest = { ...current };
      delete rest[key];
      return rest;
    });
  };

  const openProcess = async (line: ClosingScanLine) => {
    const generation = ++processReadGeneration.current;
    const docId = detail?.session.docId;
    setPendingProcess(line);
    setZeroPackingVerified(false);
    setProcessMemo('');
    setProcessGap(null);
    setProcessLoaded(false);
    if (line.orderId == null) {
      return;
    }
    try {
      const order = await client.getOrderDetailForOutbound(line.orderId);
      if (generation !== processReadGeneration.current) {
        return;
      }
      // A held order is refused by the server whatever else holds, so the hold is named before any request.
      const hold = describeHold(order.header);
      setProcessGap(
        hold ??
          (outboundCarrierBlock(order.header.orderId, order.tracking, order.pendingParcels) ||
            describeInboundGap(order.lines)),
      );
      if (hold && docId) {
        noteRowRefusal(docId, line.lineNo, hold);
      }
    } catch (err) {
      if (generation === processReadGeneration.current) {
        setProcessGap(describeRequestFailure(err));
      }
    } finally {
      if (generation === processReadGeneration.current) {
        setProcessLoaded(true);
      }
    }
  };

  const processOrder = async () => {
    if (!detail || !pendingProcess || !processLoaded || processGap || !zeroPackingVerified) {
      return;
    }
    const line = pendingProcess;
    const docId = detail.session.docId;
    const memo = processMemo.trim();
    const fingerprint = JSON.stringify([docId, line.lineNo]);
    const idempotencyKey = processKeys.current.keyFor(fingerprint);
    closeProcess();
    setBusy(true);
    try {
      let result: ProcessClosingScanOrderResponse;
      try {
        if (line.orderId == null) {
          throw new OutboundCarrierGateError('Order could not be verified — refresh before outbound.');
        }
        const block = await readOutboundCarrierBlock(line.orderId, (id) => client.getOrderDetailForOutbound(id));
        if (block) {
          throw new OutboundCarrierGateError(block);
        }
        result = await client.processClosingScanOrder(docId, line.lineNo, {
          zeroPackingVerified: true,
          idempotencyKey,
          ...(memo ? { memo } : {}),
        });
      } catch (err) {
        const refusal = describeRequestFailure(err);
        showError(refusal);
        FulfillmentSound.failure();
        noteRowRefusal(docId, line.lineNo, refusal);
        // A refused or cut request may still have moved the row, so the log shows what the server now holds.
        await loadSession(docId).catch(() => undefined);
        return;
      }
      // The outbound stands once the server answers, so a failed reload below must not read as a failed outbound.
      if (!result.alreadyOutbounded) FulfillmentSound.success();
      noteRowRefusal(docId, line.lineNo, null);
      try {
        // A newer read superseded this one, so the row on screen may predate the resolution: keep the key.
        if (await loadSession(docId)) {
          processKeys.current.release(fingerprint);
        }
        reportSuccess(describeProcessed(result));
      } catch (err) {
        // The key stays, so a retry over the stale row replays this result instead of being refused.
        showError(
          `${describeProcessed(result)}, but the session could not be reloaded — press Refresh. ${describeRequestFailure(err)}`,
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const startAmendment = async () => {
    const session = pendingAmend;
    if (!session) return;
    setPendingAmend(null);
    setBusy(true);
    setBlocked(null);
    try {
      await client.startClosingAmendment(session.docId);
      await loadSession(session.docId);
      await refreshSessions();
      reportSuccess(`Amending the ${formatSessionDay(session.startedAt)} closing`);
    } catch (err) {
      reportFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const exitAmendment = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await client.exitClosingAmendment(detail.session.docId);
      await loadSession(detail.session.docId);
      await refreshSessions();
      reportSuccess('Amendment closed — the confirmed record stands');
    } catch (err) {
      reportFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const csv = await client.exportClosingCsv(detail.session.docId);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${detail.session.docId}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      reportFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const confirmSession = async (force: boolean) => {
    if (!detail) return;
    setBusy(true);
    setHeldRefusal(null);
    try {
      await client.confirmClosing(detail.session.docId, {
        ...(force ? { force: true } : {}),
      });
      setBlocked(null);
      setHeldRefusal(null);
      await loadSession(detail.session.docId);
      await refreshSessions();
      reportSuccess(force ? 'Day closed over the standing warnings' : 'Day closed — every box verified');
    } catch (err) {
      const failure = closingErrorOf(err);
      const body = failure?.response?.status === 409 ? failure.response.data : undefined;
      // A hold is not one of the blockers a confirm can be forced over, whatever counts the body also carries.
      const refusal = body && body.code !== ORDER_ON_HOLD ? blockOf(body) : null;
      setBlocked(refusal);
      if (body?.code === ORDER_ON_HOLD) {
        setHeldRefusal({ docId: detail.session.docId, lineNos: heldLineNosOf(body) });
      }
      // A 409 that names no blocker is a different refusal — resuming, already confirmed — and reads as itself.
      if (!refusal) {
        const text = describeConfirmRefusal(err, detail.lines);
        const standing = confirmRefusal.current;
        FulfillmentSound.failure();
        // Pressing confirm again over the same refusal answers nothing new, so it is not stacked a second time.
        if (standing?.text !== text || !standingToasts.current.some((toast) => toast.id === standing.toastId)) {
          confirmRefusal.current = { text, toastId: showError(text) };
        }
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" aria-label="Loading closing session" />
      </div>
    );
  }

  const tallies = detail?.tallies;
  const snapshot = detail?.snapshot ?? null;
  // A confirmed day is the record the confirm froze; recounting the log would rewrite it [T15].
  const closedRecord =
    detail && detail.session.status !== 'OPEN' && detail.session.status !== 'AMENDING' ? snapshot : null;
  const expected = detail?.session.expectedCount ?? 0;
  const okCount = tallies?.ok ?? 0;
  const warningCount = tallies?.warnings ?? 0;
  const overScanned = overScanOf(tallies);
  // Signed for the wording helpers, which say "short" or "over"; the tiles read the clamped pair [D-f].
  const shortfall = (tallies?.remaining ?? Math.max(0, expected - okCount)) - overScanned;
  // An over-scan already shouts through the counts, so the bar stays inside its own range.
  const okShare = Math.min(100, percentOf(okCount, expected));
  const warningShare = Math.min(100 - okShare, percentOf(warningCount, expected));
  const reconciled = shortfall === 0;
  const amending = detail?.session.status === 'AMENDING';
  // Re-confirming carries the same gate wording; only the verb changes, so the blockers read the same.
  const confirmVerb = amending ? 'Re-confirm closing' : 'Confirm closing';
  const confirmBlockers = describeConfirmBlockers(warningCount, shortfall);
  // The force dialog names what it signs over: the live count, or the server's once only its refusal said so.
  const forcedWarnings = warningCount > 0 ? warningCount : (blocked?.warnings ?? 0);
  const forcedWarningsLabel =
    forcedWarnings > 0 ? pluralise(forcedWarnings, 'unresolved warning') : 'unresolved warnings';
  const tiles: TallyTile[] = [
    { label: 'Outbound target', value: expected },
    { label: 'Scan', value: tallies?.scanned ?? 0 },
    { label: 'Outbounded', value: tallies?.ok ?? 0 },
    { label: 'Warning', value: tallies?.warnings ?? 0 },
    { label: 'Duplicate', value: tallies?.duplicates ?? 0 },
    { label: 'Unknown order', value: tallies?.unknown ?? 0 },
    { label: 'Scans left', value: tallies?.remaining ?? 0 },
    // Clamping remaining at zero would hide an over-scan, so it gets a tile of its own [D-f].
    { label: 'Over-scanned', value: overScanned, danger: overScanned > 0 },
  ];

  // Oldest first: the floor reads the log the way it scanned, with each new row appended at the bottom.
  const scanLog = detail ? [...detail.lines].sort((a, b) => a.lineNo - b.lineNo) : [];
  // What a cancel gives up: the rows still in the working count, deleted ones already having left it.
  const liveScans = scanLog.filter((line) => line.verdict !== 'DELETED').length;
  const raisedWarnings = {
    notOutbounded: scanLog.filter((line) => line.verdict === 'NOT_OUTBOUNDED').length,
    // The two newer unknowns block a confirm exactly as the original does, so they are counted with it.
    unknown: scanLog.filter((line) => UNKNOWN_VERDICTS.includes(line.verdict)).length,
    duplicate: scanLog.filter((line) => line.verdict === 'DUPLICATE').length,
    onHold: scanLog.filter((line) => line.verdict === 'ON_HOLD').length,
  };
  // Clean rows the last hold refusal named and the desk has not removed yet. They are only shown: the force stays on offer,
  // because pressing it is how the server is asked again once a hold is cleared on Order Detail.
  const heldAfterScan =
    detail && heldRefusal?.docId === detail.session.docId
      ? heldRefusal.lineNos.filter((lineNo) =>
          detail.lines.some(
            (line) => line.lineNo === lineNo && line.verdict !== 'DELETED' && line.verdict !== 'ON_HOLD',
          ),
        )
      : [];
  const raisedTotal =
    raisedWarnings.notOutbounded + raisedWarnings.unknown + raisedWarnings.duplicate + raisedWarnings.onHold;

  return (
    <Container
      fluid
      className="py-3"
      onClick={(event) => {
        // A click on a heading or a tile is not a control the desk means to keep; the scanner gets it back.
        if (!(event.target as HTMLElement).closest(CURSOR_OWNERS)) {
          scanInput.reclaim();
        }
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-1">Daily Closing</h2>
          <div className="text-muted small">Scan the outbounded boxes to close the day</div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" disabled={busy} onClick={() => void refreshView()}>
            Refresh
          </Button>
          <Link to={ROUTES.fulfillment.orders} className="btn btn-outline-secondary btn-sm">
            Back to orders
          </Link>
        </div>
      </div>

      {/* One feedback slot for the whole screen; every action on it reports here and nowhere else [D-t]. */}
      <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />

      {(retryDelete || closedDay) && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {retryDelete && (
            <Button variant="outline-danger" size="sm" disabled={busy} onClick={() => void deleteScan(retryDelete)}>
              Try again
            </Button>
          )}
          {/* A start refused because that day is already closed has exactly one correct next move. */}
          {closedDay && (
            <Button
              variant="outline-dark"
              size="sm"
              disabled={busy}
              onClick={() => {
                const target = closedDay;
                setClosedDay(null);
                void refreshSessions().then((listed) =>
                  setPendingAmend(listed.sessions.find((session) => session.docId === target.docId) ?? null),
                );
              }}
            >
              Amend {closedDay.operatingDay}
            </Button>
          )}
        </div>
      )}

      {!detail && (
        <Card className="mb-3">
          <Card.Header className="fw-semibold">1. Start the closing session</Card.Header>
          <Card.Body className="d-flex flex-wrap gap-3 align-items-end">
            <Form.Group controlId="closing-expected-count">
              <Form.Label className="text-muted small text-uppercase mb-1">
                Boxes to outbound today (manual count)
              </Form.Label>
              <Form.Control
                size="sm"
                type="number"
                min={0}
                value={expectedCount}
                onChange={(e) => setExpectedCount(e.target.value)}
              />
              <Form.Text muted>Enter the number of packed boxes you counted, then start the closing.</Form.Text>
            </Form.Group>
            <Button
              variant="primary"
              size="sm"
              disabled={busy || !isValidExpectedCount(expectedCount)}
              onClick={() => void startSession()}
            >
              Start closing
            </Button>
          </Card.Body>
        </Card>
      )}

      {detail && (
        <>
          {amending && (
            <Alert variant="warning" className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <div className="fw-semibold">
                  AMENDING — {formatSessionDay(detail.session.startedAt)} closing (confirmed {okCount})
                </div>
                <div className="small">
                  The confirmed record stays until you re-confirm — raise the target, scan the found parcel, and press
                  Re-confirm closing (exact match + 0 warnings required)
                </div>
              </div>
              <Button variant="outline-dark" size="sm" disabled={busy} onClick={() => void exitAmendment()}>
                ✕ Exit amendment
              </Button>
            </Alert>
          )}

          {joined?.docId === detail.session.docId && (
            <Alert variant="info" className="py-2 small" dismissible onClose={() => setJoined(null)}>
              {joined.text}
            </Alert>
          )}

          <Card className="mb-3">
            <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <span className="text-muted small text-uppercase me-2">Session</span>
                <span className="font-monospace">{detail.session.docId}</span>
                <span className="text-muted small ms-3">
                  Operator {detail.session.operator ?? '—'} · started {formatScanTime(detail.session.startedAt)}
                </span>
                {targetDraft == null ? (
                  <span className="small ms-3">Boxes to outbound today {detail.session.expectedCount ?? '—'}</span>
                ) : (
                  <span className="small ms-3 d-inline-flex align-items-center gap-2">
                    Boxes to outbound today
                    <Form.Control
                      size="sm"
                      type="number"
                      min={1}
                      style={{ width: '7rem' }}
                      aria-label="Manual count"
                      autoFocus
                      value={targetDraft}
                      onChange={(e) => setTargetDraft(e.target.value)}
                    />
                  </span>
                )}
                {targetError && <span className="small ms-2 text-danger">{targetError}</span>}
                {/* The target stays editable for as long as the session is being scanned into (BR-14). */}
                {isScannable(detail.session.status) &&
                  (targetDraft == null ? (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="ms-3"
                      disabled={busy}
                      onClick={() => {
                        setTargetError(null);
                        setTargetBase(targetBaseOf(detail.session));
                        setTargetDraft(String(detail.session.expectedCount ?? ''));
                      }}
                    >
                      ↺ Edit count
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      className="ms-3"
                      disabled={busy}
                      onClick={() => void saveTarget()}
                    >
                      Save
                    </Button>
                  ))}
                {isScannable(detail.session.status) && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-2"
                    disabled={busy}
                    onClick={() => setCancelling(true)}
                  >
                    ✕ Cancel Closing
                  </Button>
                )}
              </div>
              <div className="d-flex align-items-center gap-3">
                <Badge bg={detail.session.status === 'OPEN' ? 'dark' : 'success'}>{detail.session.status}</Badge>
                {/* Only a closed session is dismissable — the day's own session stays put under the scanner. */}
                {detail.session.status !== 'OPEN' && (
                  <Button variant="outline-secondary" size="sm" disabled={busy} onClick={() => void closeView()}>
                    Close this view
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          <Row className="g-3 mb-3">
            {tiles.map((tile) => (
              <Col key={tile.label} xs={6} md={4} xl={2}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted small text-uppercase">{tile.label}</div>
                    <div className={`fs-4 fw-semibold${tile.danger ? ' text-danger' : ''}`}>{tile.value}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="mb-3">
            <Card.Body>
              <div
                className="progress"
                role="progressbar"
                aria-label="Closing progress"
                aria-valuenow={okShare}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="progress-bar bg-success" style={{ width: `${okShare}%` }} />
                <div className="progress-bar bg-danger" style={{ width: `${warningShare}%` }} />
              </div>
              <div className="text-muted small mt-2">
                {describeProgress(okCount, warningCount, expected, shortfall)}
              </div>
            </Card.Body>
          </Card>

          {isScannable(detail.session.status) && (
            // Pinned in view: a browser scrolls a focused box back into view as a scanner types into it, so an
            // operator reading the newest rows at the bottom of a long log was thrown back up on every scan.
            <Card className="mb-3 sticky-top" data-testid="closing-scan-desk">
              <Card.Body>
                {lastScan?.docId === detail.session.docId && (
                  <Alert variant={lastScan.verdict === 'OK' ? 'success' : 'danger'} className="py-2">
                    {VERDICT_BADGES[lastScan.verdict]?.label ?? lastScan.verdict} — {lastScan.waybill}
                  </Alert>
                )}
                <div className="d-flex align-items-center gap-3 mb-2">
                  <Form.Check
                    type="switch"
                    id="closing-voice"
                    label="Voice alert"
                    checked={voiceOn}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setMuting(true);
                        return;
                      }
                      setVoiceOn(true);
                      // Turning it off is confirmed in the modal and stays silent, as a muted alert should.
                      speak(VOICE_ENABLED);
                    }}
                  />
                  <Button variant="outline-secondary" size="sm" disabled={!voiceOn} onClick={() => speakWarning()}>
                    Test voice
                  </Button>
                  <span className="text-muted small">Reads each warning aloud in the words the row raised.</span>
                  {/* The scanner types into whatever holds the cursor, so a lost cursor has to be visible. */}
                  {scannerIdle && (
                    <span className="badge bg-warning text-dark" role="status">
                      ⏸ Scanner idle — click the box to scan
                    </span>
                  )}
                </div>
                <Form onSubmit={(e) => void submitScan(e)}>
                  <Form.Group controlId="closing-waybill">
                    <Form.Label className="text-muted small text-uppercase mb-1">Waybill barcode</Form.Label>
                    <CameraScanField label="Scan waybill with camera">
                      <Form.Control
                        ref={scanInput.ref}
                        autoFocus
                        placeholder="Scan a barcode"
                        value={waybill}
                        // A click on the box itself drops the caret where it lands; a leftover must be overwritten [G-1].
                        onClick={scanInput.reclaim}
                        onFocus={() => setScannerIdle(false)}
                        onBlur={() => setScannerIdle(true)}
                        {...asciiScanField(
                          (value) => {
                            scanInput.noteTyping();
                            setWaybill(value);
                          },
                          // Scanners that end a code with Tab would otherwise leave the box without sending it.
                          (e) => {
                            if (e.key === 'Tab' && waybill.trim()) {
                              e.preventDefault();
                              void submitScan(e);
                            }
                          },
                        )}
                      />
                    </CameraScanField>
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
          )}

          <Card className="mb-3">
            <Card.Header className="fw-semibold">Scan log ({scanLog.length})</Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Scanned at</th>
                    <th>Waybill barcode</th>
                    <th>Order ID</th>
                    <th className="text-end">Items</th>
                    <th>Closing verdict</th>
                    <th>Operator</th>
                    <th>Remark</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {scanLog.map((line) => {
                    const badge = VERDICT_BADGES[line.verdict] ?? { variant: 'secondary', label: line.verdict };
                    const deleted = line.verdict === 'DELETED';
                    const heldLate = heldAfterScan.includes(line.lineNo);
                    const warned = WARNING_VERDICTS.includes(line.verdict) || heldLate;
                    const rowRefusal = rowRefusals[rowKeyOf(detail.session.docId, line.lineNo)];
                    const exited = EXITED_ORDER_STATES.includes(line.fulfillmentStatus ?? '');
                    const rowClass = deleted
                      ? 'text-decoration-line-through text-muted'
                      : warned
                        ? 'table-danger'
                        : undefined;
                    return (
                      <tr key={line.lineNo} className={rowClass}>
                        <td>{line.lineNo}</td>
                        <td className="text-nowrap font-monospace">{formatScanTime(line.scannedAt)}</td>
                        <td className="font-monospace">{line.waybill}</td>
                        <td>
                          {line.orderId != null ? (
                            <Link
                              to={ROUTES.fulfillment.orderDetail(line.orderId)}
                              className="font-monospace"
                              // The scan log stays where it was, so the desk can come back to the same row.
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {line.orderId}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="text-end">{line.totalQuantity ?? '—'}</td>
                        <td>
                          <Badge bg={badge.variant} text={badge.variant === 'warning' ? 'dark' : 'light'}>
                            {badge.label}
                          </Badge>
                          {warned && rowRefusal && <div className="small text-danger mt-1">{rowRefusal}</div>}
                        </td>
                        <td>{line.operator ?? '—'}</td>
                        <td className="small">
                          {line.verdict === 'NOT_OUTBOUNDED' && (
                            <>
                              {line.fulfillmentStatus ?? line.warning ?? ''}
                              {exited && ` — ${EXITED_ORDER_REMARK}`}
                              {/* Resolution happens in the row, which keeps the operator inside the scan loop. */}
                              {isScannable(detail.session.status) && line.orderId != null && !exited && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="ms-2"
                                  disabled={busy}
                                  onClick={() => void openProcess(line)}
                                >
                                  Process this order
                                </Button>
                              )}
                            </>
                          )}
                          {line.verdict === 'DUPLICATE' && describeFirstScan(line)}
                          {line.verdict === 'UNKNOWN_WAYBILL' && UNKNOWN_WAYBILL_REMARK}
                          {line.verdict === 'ORDER_NOT_FOUND' && ORDER_NOT_FOUND_REMARK}
                          {line.verdict === 'AMBIGUOUS_WAYBILL' && AMBIGUOUS_WAYBILL_REMARK}
                          {line.verdict === 'ON_HOLD' && ON_HOLD_REMARK}
                          {heldLate && HELD_AFTER_SCAN_REMARK}
                        </td>
                        <td className="text-end">
                          {isScannable(detail.session.status) && !deleted && (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              disabled={busy}
                              onClick={() => setPendingDelete(line)}
                            >
                              ✕
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {scanLog.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No boxes scanned yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {blocked && (
            <Alert variant="danger">
              <div className="fw-semibold">Closing is blocked</div>
              {blocked.reasons.map((reason) => (
                <div key={reason}>{describeBlockReason(blocked, reason)}</div>
              ))}
            </Alert>
          )}

          {closedRecord && (
            <Alert
              variant={closedRecord.forced || closedRecord.warnings > 0 ? 'warning' : 'success'}
              className="d-flex flex-wrap justify-content-between align-items-center gap-3"
              role="group"
              aria-label={`Closing record ${formatSessionDay(detail.session.startedAt)}`}
            >
              <div>
                <div className="fw-semibold">{describeRecord(closedRecord)}</div>
                <div>{describeRecordCounts(closedRecord)}</div>
                <div className="small text-muted">
                  v{closedRecord.version} · signed off by {closedRecord.signedOffBy} ·{' '}
                  {formatScanTime(closedRecord.confirmedAt)}
                </div>
              </div>
            </Alert>
          )}

          {closedRecord && (
            <Card className="mb-3">
              <Card.Header className="fw-semibold">Warning resolution summary</Card.Header>
              <Card.Body className="small">
                {`${pluralise(raisedTotal, 'warning')} in the scan log — ${raisedWarnings.notOutbounded} not outbounded · ${raisedWarnings.unknown} unknown order · ${raisedWarnings.duplicate} duplicate scan · ${raisedWarnings.onHold > 0 ? `${raisedWarnings.onHold} on hold · ` : ''}${closedRecord.warnings} still standing at sign-off`}
              </Card.Body>
            </Card>
          )}

          {/* The report belongs to the record, so it survives the session going back into amendment. */}
          {(snapshot != null || (detail.session.version ?? 0) > 0) && (
            <Card className="mb-3">
              <Card.Body>
                <Button variant="outline-dark" size="sm" disabled={busy} onClick={() => void downloadCsv()}>
                  Download closing report (CSV)
                </Button>
              </Card.Body>
            </Card>
          )}

          {/* Confirm-time controls are gone once the day is closed; there is nothing left to confirm. */}
          {isScannable(detail.session.status) && (
            <Card>
              <Card.Body className="d-flex flex-wrap gap-3 align-items-end">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy || confirmBlockers.length > 0}
                  onClick={() => void confirmSession(false)}
                >
                  {confirmBlockers.length > 0 ? `${confirmVerb} (${confirmBlockers.join(' · ')})` : confirmVerb}
                </Button>
                {raisedWarnings.onHold > 0 && (
                  <span className="small text-danger align-self-center">
                    {raisedWarnings.onHold === 1
                      ? 'A box on hold cannot be confirmed over. Remove its scan and lower the count by one.'
                      : `${raisedWarnings.onHold} boxes on hold cannot be confirmed over. Remove their scans and lower the count by ${raisedWarnings.onHold}.`}
                  </span>
                )}
                {/* A mismatch is never forced — FS-097 wants an over-scan to fail as hard as a short one. */}
                {/* A held box must not reach the carrier, so "confirm anyway" is not on offer while one stands [SSD-7914]. */}
                {reconciled &&
                  raisedWarnings.onHold === 0 &&
                  (warningCount > 0 || blocked?.reasons.includes('WARNINGS')) &&
                  !blocked?.reasons.includes('COUNT_MISMATCH') && (
                    <Button variant="outline-danger" size="sm" disabled={busy} onClick={() => setPendingForce(true)}>
                      Confirm despite warnings
                    </Button>
                  )}
              </Card.Body>
            </Card>
          )}
        </>
      )}

      <Card className="mt-3">
        <Card.Header className="fw-semibold">Closing history</Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle" aria-label="Closing history">
            <thead className="table-dark">
              <tr>
                <th>Session</th>
                <th>Day</th>
                <th>Status</th>
                <th className="text-end">Manual count</th>
                <th>Operator</th>
                <th>Signed off by</th>
                <th>Warnings</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.docId}>
                  <td className="font-monospace">{session.docId}</td>
                  <td className="text-nowrap">{formatSessionDay(session.startedAt)}</td>
                  <td>
                    <Badge bg={session.status === 'OPEN' ? 'dark' : 'success'}>{session.status}</Badge>
                  </td>
                  <td className="text-end">{session.expectedCount ?? '—'}</td>
                  <td>{session.operator ?? '—'}</td>
                  <td>{session.signedOffBy ?? '—'}</td>
                  <td>
                    <HistoryWarnings session={session} />
                  </td>
                  <td className="text-end">
                    {/* A day already amended says which version stands, so the row is not read as the original. */}
                    {(session.version ?? 0) > 1 && (
                      <Badge bg="warning" text="dark" className="me-2">
                        Amended v{session.version}
                      </Badge>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={busy || session.docId === detail?.session.docId}
                      onClick={() => void viewSession(session.docId)}
                    >
                      Open
                    </Button>
                    {session.status === 'CONFIRMED' && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ms-2"
                        disabled={busy}
                        onClick={() => setPendingAmend(session)}
                      >
                        Amend
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No closing session has been opened yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={openElsewhere != null} onHide={() => setOpenElsewhere(null)} centered>
        <Modal.Body>
          <div className="fw-semibold">A closing session is already open</div>
          {openElsewhere && (
            <div className="text-muted small mt-1">
              Started by {openElsewhere.session.operator ?? '—'} · {kstTimestamp(openElsewhere.session.startedAt)} ·
              manual count {openElsewhere.session.expectedCount ?? '—'} · {openElsewhere.tallies.scanned} scanned
            </div>
          )}
          <div className="small mt-2">
            Only one closing runs at a time. Continue that session instead of starting another.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setOpenElsewhere(null)}>
            Not now
          </Button>
          <Button variant="primary" size="sm" disabled={busy} onClick={() => void continueOpenSession()}>
            Continue this session
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={pendingTarget != null} onHide={() => setPendingTarget(null)} centered>
        <Modal.Body>
          <div className="fw-semibold">
            {pendingTarget && `Set the manual count to ${formatCount(pendingTarget.count)} boxes?`}
          </div>
          <div className="text-muted small mt-1">
            That is more than {formatCount(LARGE_TARGET)} boxes for one day. Check the count before saving it.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setPendingTarget(null)}>
            Fix the count
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            onClick={() => {
              const target = pendingTarget;
              setPendingTarget(null);
              if (target?.action === 'start') {
                void startSession(true);
              } else if (target?.action === 'edit') {
                void saveTarget(true);
              }
            }}
          >
            {pendingTarget && `Yes — ${formatCount(pendingTarget.count)} boxes`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={pendingForce} onHide={() => setPendingForce(false)} centered>
        <Modal.Body>
          <div className="fw-semibold">Confirm the closing with {forcedWarningsLabel}?</div>
          <div className="text-muted small mt-1">
            The day is recorded as closed with {forcedWarningsLabel} still standing. They stay on the closing record and
            in the report.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setPendingForce(false)}>
            Go back
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={busy}
            onClick={() => {
              setPendingForce(false);
              void confirmSession(true);
            }}
          >
            Yes — confirm despite warnings
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={pendingAmend != null} onHide={() => setPendingAmend(null)} centered>
        <Modal.Header>
          <Modal.Title as="div" className="fs-6 fw-semibold">
            Amend Closing — {formatSessionDay(pendingAmend?.startedAt ?? null)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fw-semibold">
            Amend the closing for {formatSessionDay(pendingAmend?.startedAt ?? null)}? The confirmed record stays until
            you re-confirm.
          </div>
          {pendingAmend && (
            <div className="text-muted small mt-1">
              Confirmed {pendingAmend.expectedCount ?? '—'} · {formatScanTime(pendingAmend.confirmedAt)} (
              {pendingAmend.signedOffBy ?? '—'})
            </div>
          )}
          <Alert variant="info" className="mt-3 mb-0 py-2 small">
            Amendment mode reloads the day&apos;s confirmed scan list with the scan input active. Raise the manual
            target, scan the found parcel, and press Re-confirm closing — the record is updated in place with an Amended
            badge.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setPendingAmend(null)}>
            Keep the record
          </Button>
          <Button variant="primary" size="sm" disabled={busy} onClick={() => void startAmendment()}>
            Amend — open the closing
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={pendingProcess != null} onHide={closeProcess} centered>
        <Modal.Header>
          <Modal.Title as="div" className="fs-6 fw-semibold">
            Process Processing Order — Order {pendingProcess?.orderId}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fw-semibold">This order has not been outbound-processed yet.</div>
          {pendingProcess && (
            <div className="text-muted small mt-1">
              Tracking {pendingProcess.waybill} · {pendingProcess.totalQuantity ?? '—'} item(s) · Status{' '}
              {pendingProcess.fulfillmentStatus ?? '—'}
            </div>
          )}
          {/* The packing video this check asks about, so nobody has to open Zero Packing to tick it (SSD-7372). */}
          {pendingProcess?.orderId != null && <PackingVideoInline client={client} orderId={pendingProcess.orderId} />}
          <Form.Check
            className="mt-3"
            type="checkbox"
            id="closing-zero-packing"
            label="Packing status of this order verified at Zero Packing — check when proper packing is confirmed"
            checked={zeroPackingVerified}
            onChange={(e) => setZeroPackingVerified(e.target.checked)}
          />
          <Form.Group className="mt-3" controlId="closing-process-memo">
            <Form.Label className="text-muted small text-uppercase mb-1">Memo (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={processMemo}
              onChange={(e) => setProcessMemo(e.target.value)}
              placeholder="Packing status and actions taken — if written, it is also recorded to the order's comments"
            />
          </Form.Group>
          {processGap && (
            <Alert variant="warning" className="mt-3 mb-0 py-2 small">
              {processGap}
            </Alert>
          )}
          <Alert variant="info" className="mt-3 mb-0 py-2 small">
            Processing Outbound changes the real order status, reflected on every screen. In the closing list this row
            is re-judged as Outbounded. If packing is incomplete, handle it separately and rescan.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={closeProcess}>
            Close
          </Button>
          <Button
            variant="success"
            size="sm"
            disabled={busy || !zeroPackingVerified || !processLoaded || processGap != null}
            onClick={() => void processOrder()}
          >
            Process Outbound → resolve warning
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={cancelling} onHide={() => setCancelling(false)} centered>
        <Modal.Body>
          <div className="fw-semibold">Cancel today&apos;s closing?</div>
          <div className="text-muted small mt-1">
            {/* Cancel always asks, so at zero scans the clause about them is simply left out. */}
            {liveScans > 0 && `${pluralise(liveScans, 'scan')} will be removed from this session. `}
            The scan records are kept in the audit log and can be reviewed later, but the session will restart from the
            count entry.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setCancelling(false)}>
            Keep scanning
          </Button>
          <Button variant="danger" size="sm" disabled={busy} onClick={() => void cancelSession()}>
            Yes — cancel closing
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={muting} onHide={() => setMuting(false)} centered>
        <Modal.Body>
          <div className="fw-semibold">Turn the voice alert off?</div>
          <div className="text-muted small mt-1">
            Warnings would then only appear on screen, and the desk scans with its eyes on the parcels.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setMuting(false)}>
            Keep it on
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setVoiceOn(false);
              setMuting(false);
            }}
          >
            Yes — turn it off
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={pendingDelete != null} onHide={() => setPendingDelete(null)} centered>
        <Modal.Body>
          <div className="fw-semibold">Remove this scan record?</div>
          {pendingDelete && (
            <div className="text-muted small mt-1 font-monospace">
              #{pendingDelete.lineNo} · {pendingDelete.waybill}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setPendingDelete(null)}>
            No
          </Button>
          <Button variant="danger" size="sm" onClick={() => pendingDelete && void deleteScan(pendingDelete)}>
            Yes — remove
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
